import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { CardService } from '../../services/cards';
import { VirtualCard, Cardholder } from '../../types';
import { safeArray } from '../../utils/money';

// 异步actions
export const fetchCards = createAsyncThunk(
  'card/fetchCards',
  async (params: { page?: number; limit?: number; filters?: any }, { rejectWithValue }) => {
    try {
      const response = await CardService.listCards(params.page, params.limit, params.filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取虚拟卡列表失败');
    }
  }
);

export const fetchCardholders = createAsyncThunk(
  'card/fetchCardholders',
  async (params: { page?: number; limit?: number; search?: string }, { rejectWithValue }) => {
    try {
      const response = await CardService.listCardholders(params.page, params.limit, params.search);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取持卡人列表失败');
    }
  }
);

export const createCard = createAsyncThunk(
  'card/createCard',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await CardService.createCard(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '创建虚拟卡失败');
    }
  }
);

export const createCardholder = createAsyncThunk(
  'card/createCardholder',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await CardService.createCardholder(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '创建持卡人失败');
    }
  }
);

export const updateCardStatus = createAsyncThunk(
  'card/updateStatus',
  async (params: { id: string; action: 'activate' | 'block' | 'unblock' | 'cancel'; reason?: string }, { rejectWithValue }) => {
    try {
      let response;
      switch (params.action) {
        case 'activate':
          response = await CardService.activateCard(params.id);
          break;
        case 'block':
          response = await CardService.blockCard(params.id, params.reason);
          break;
        case 'unblock':
          response = await CardService.unblockCard(params.id);
          break;
        case 'cancel':
          await CardService.cancelCard(params.id, params.reason);
          return { id: params.id, status: 'canceled' };
        default:
          throw new Error('Invalid action');
      }
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '更新卡片状态失败');
    }
  }
);

export const fetchCardStats = createAsyncThunk(
  'card/fetchStats',
  async (period: 'day' | 'week' | 'month' | 'year' = 'month', { rejectWithValue }) => {
    try {
      const response = await CardService.getCardStats(period);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取卡片统计失败');
    }
  }
);

interface CardState {
  cards: {
    data: VirtualCard[];
    pagination: any;
    loading: boolean;
  };
  cardholders: {
    data: Cardholder[];
    pagination: any;
    loading: boolean;
  };
  stats: any;
  selectedCards: string[];
  creating: boolean;
  updating: boolean;
  error: string | null;
}

const initialState: CardState = {
  cards: {
    data: [],
    pagination: null,
    loading: false,
  },
  cardholders: {
    data: [],
    pagination: null,
    loading: false,
  },
  stats: null,
  selectedCards: [],
  creating: false,
  updating: false,
  error: null,
};

const cardSlice = createSlice({
  name: 'card',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedCards: (state, action: PayloadAction<string[]>) => {
      state.selectedCards = action.payload;
    },
    toggleCardSelection: (state, action: PayloadAction<string>) => {
      const cardId = action.payload;
      if (state.selectedCards.includes(cardId)) {
        state.selectedCards = state.selectedCards.filter(id => id !== cardId);
      } else {
        state.selectedCards.push(cardId);
      }
    },
    clearSelection: (state) => {
      state.selectedCards = [];
    },
  },
  extraReducers: (builder) => {
    // 获取虚拟卡列表
    builder
      .addCase(fetchCards.pending, (state) => {
        state.cards.loading = true;
        state.error = null;
      })
      .addCase(fetchCards.fulfilled, (state, action) => {
        state.cards.loading = false;
        state.cards.data = safeArray<VirtualCard>(action.payload.data);
        state.cards.pagination = action.payload.pagination;
      })
      .addCase(fetchCards.rejected, (state, action) => {
        state.cards.loading = false;
        state.error = action.payload as string;
      });

    // 获取持卡人列表
    builder
      .addCase(fetchCardholders.pending, (state) => {
        state.cardholders.loading = true;
        state.error = null;
      })
      .addCase(fetchCardholders.fulfilled, (state, action) => {
        state.cardholders.loading = false;
        state.cardholders.data = safeArray<Cardholder>(action.payload.data);
        state.cardholders.pagination = action.payload.pagination;
      })
      .addCase(fetchCardholders.rejected, (state, action) => {
        state.cardholders.loading = false;
        state.error = action.payload as string;
      });

    // 创建虚拟卡
    builder
      .addCase(createCard.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createCard.fulfilled, (state, action: PayloadAction<VirtualCard>) => {
        state.creating = false;
        state.cards.data.unshift(action.payload);
      })
      .addCase(createCard.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // 创建持卡人
    builder
      .addCase(createCardholder.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createCardholder.fulfilled, (state, action: PayloadAction<Cardholder>) => {
        state.creating = false;
        state.cardholders.data.unshift(action.payload);
      })
      .addCase(createCardholder.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // 更新卡片状态
    builder
      .addCase(updateCardStatus.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateCardStatus.fulfilled, (state, action) => {
        state.updating = false;
        const updatedCard = action.payload;
        const index = state.cards.data.findIndex(card => card.id === updatedCard.id);
        if (index !== -1) {
          state.cards.data[index] = { ...state.cards.data[index], status: updatedCard.status as any };
        }
      })
      .addCase(updateCardStatus.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });

    // 获取卡片统计
    builder
      .addCase(fetchCardStats.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchCardStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(fetchCardStats.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setSelectedCards, toggleCardSelection, clearSelection } = cardSlice.actions;
export default cardSlice.reducer; 